import React, { useState, useEffect } from "react";
import { Form, Input, Button, message, Table, Popconfirm, Checkbox } from "antd";
import { EditOutlined } from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import { CustomListService } from "../../Services/CustomListService";
import { WebApiCustomListsDtoCreateCustomListDto, WebApiCustomListsDtoCustomListDto } from "../../Repository/eventbookyapi";
import LayoutLeftMenu from "../../Components/Layout/LayoutLeftMenu";
import LeftMenuSettings from "./LeftSettings";
import { TextService } from "../../Services/TextService";
import Title from "antd/es/typography/Title";
import { getCurrentLanguage } from "../../Helpers/RouteHelper";
import { SubscriptionLevel } from "../../Types/SubscriptionLevel";
import EditCustomListModal from "./EditCustomListModal";
import { MembersEditRights } from "../../Types/MembersEditRights";

const CustomListSettings: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [lists, setLists] = useState<WebApiCustomListsDtoCustomListDto[]>([]);
    const [tableLoading, setTableLoading] = useState(false);
    const [editModalOpen, setEditModalOpen] = useState(false);
    const [selectedList, setSelectedList] = useState<WebApiCustomListsDtoCustomListDto | null>(null);
    const navigate = useNavigate();
    const GetText = TextService.GetText;

    const fetchLists = async () => {
        setTableLoading(true);
        try {
            const data = await CustomListService.GetCustomLists();
            setLists(data);
        } catch {
            message.error("Kunde inte hämta listor.");
        }
        setTableLoading(false);
    };

    const handleDelete = async (id: number) => {
        setTableLoading(true);
        try {
            await CustomListService.DeleteCustomList(id);
            message.success("Listan har tagits bort!");
            fetchLists();
        } catch {
            message.error("Kunde inte ta bort listan.");
        }
        setTableLoading(false);
    };

    const handleEditName = (record: WebApiCustomListsDtoCustomListDto) => {
        setSelectedList(record);
        setEditModalOpen(true);
    };

    const handleEditSuccess = () => {
        fetchLists();
    };

    useEffect(() => {
        fetchLists();
    }, []);

    const onFinish = async (values: { name: string; allowMembersEdit?: boolean }) => {
        setLoading(true);
        try {
            await CustomListService.CreateCustomList({
                name: values.name,
                membersEditRights: values.allowMembersEdit ? MembersEditRights.OwnItems : MembersEditRights.None,
            });
            message.success("Listan har skapats!");
            fetchLists();
        } catch (error) {
            message.error("Kunde inte skapa listan.");
        }
        setLoading(false);
    };

    const columns = [
        {
            title: "Namn",
            dataIndex: "name",
            key: "name",
            render: (text: string, record: WebApiCustomListsDtoCustomListDto) => (
                <span>
                    {text}
                    <EditOutlined
                        onClick={() => handleEditName(record)}
                        style={{
                            marginLeft: 8,
                            fontSize: '16px',
                            color: '#1890ff',
                            cursor: 'pointer',
                        }}
                    />
                </span>
            ),
        },
        {
            title: "Hantera",
            key: "action",
            render: (_: any, record: WebApiCustomListsDtoCustomListDto) => (
                <>
                    <Button
                        type="primary"
                        onClick={() => navigate('/' + getCurrentLanguage() + `/editCustomList/${record.id}`)}
                        size="small"
                        style={{ marginRight: 8 }}
                    >
                        Öppna
                    </Button>
                    <Popconfirm
                        title="Är du säker på att du vill ta bort listan?"
                        onConfirm={() => handleDelete(record.id!)}
                        okText="Ja"
                        cancelText="Nej"
                    >
                        <Button danger size="small">
                            Ta bort
                        </Button>
                    </Popconfirm>
                </>
            ),
        },
    ];

    return (
        <LayoutLeftMenu menu={<LeftMenuSettings selected={"customListsSettings"} />}>
            <Title level={2}>{GetText("CustomListSettings_Title")}</Title>
            <div style={{ marginBottom: 24, fontSize: 16 }}>
                {GetText("CustomList_Description")}
            </div>
            <Form layout="vertical" onFinish={onFinish} initialValues={{ allowMembersEdit: false }}>
                <Form.Item
                    label="Listnamn"
                    name="name"
                    rules={[{ required: true, message: "Ange ett namn för listan" }]}
                >
                    <Input placeholder="Namn på lista" />
                </Form.Item>
                <Form.Item
                    name="allowMembersEdit"
                    valuePropName="checked"
                >
                    <Checkbox>Tillåt medlemmar att kunna redigera</Checkbox>
                </Form.Item>
                <Form.Item>
                    <Button disabled={user!.subscriptionLevel < SubscriptionLevel.Premium} type="primary" htmlType="submit" loading={loading}>
                        Skapa lista
                    </Button>
                    {user!.subscriptionLevel < SubscriptionLevel.Enterprise && <span> Uppgradera <a href='/Pricing'>till enterprise</a> för att skapa egna datatabeller</span>}
                </Form.Item>
            </Form>
            <Table
                style={{ marginTop: 32 }}
                columns={columns}
                dataSource={lists}
                rowKey="id"
                loading={tableLoading}
                pagination={false}
            />
            {selectedList && (
                <EditCustomListModal
                    open={editModalOpen}
                    onClose={() => setEditModalOpen(false)}
                    onSuccess={handleEditSuccess}
                    customList={selectedList}
                />
            )}
        </LayoutLeftMenu>
    );
};

export default CustomListSettings;